import { useLocation } from '@reach/router';
import TawkMessengerReact from '@tawk.to/tawk-messenger-react';
import React, { FC, useCallback, useEffect, useRef } from 'react';
import { isProposalBuilderLocation } from 'shared/utils';

interface TawkApi {
  hideWidget: () => void;
  showWidget: () => void;
}

export const TawkLiveChat: FC = () => {
  const { pathname } = useLocation();
  const tawkRef = useRef<TawkApi | null>(null);
  const isTawkLoadedRef = useRef(false);
  const pathnameRef = useRef(pathname);

  /** Keep the widget mounted and toggle only its visibility, so the launcher
   * injected by Tawk is reliably hidden on the proposal builder route.
   *
   * Tawk attaches showWidget/hideWidget to window.Tawk_API only once its remote
   * script finishes loading, while the component ref is populated on mount, so
   * calling either before the tawkLoad event throws. Reads the pathname from a
   * ref because Tawk registers the load listener once on mount and would
   * otherwise invoke a stale closure. */
  const syncVisibility = useCallback(() => {
    if (!tawkRef.current || !isTawkLoadedRef.current) return;

    try {
      if (isProposalBuilderLocation(pathnameRef.current)) {
        tawkRef.current.hideWidget();
      } else {
        tawkRef.current.showWidget();
      }
    } catch (error) {
      console.error('Failed to toggle Tawk widget visibility', error);
    }
  }, []);

  const handleLoad = useCallback(() => {
    isTawkLoadedRef.current = true;
    syncVisibility();
  }, [syncVisibility]);

  useEffect(() => {
    pathnameRef.current = pathname;
    syncVisibility();
  }, [pathname, syncVisibility]);

  return (
    <TawkMessengerReact
      ref={tawkRef}
      propertyId={process.env.GATSBY_TAWK_PROPERTY_ID}
      widgetId={process.env.GATSBY_TAWK_WIDGET_ID}
      onLoad={handleLoad}
    />
  );
};
